<template>
  <div
    class="metric-card"
    :class="cardClasses"
    :style="highlightStyle"
    :aria-label="ariaLabel || `${title}: ${value}${subtitle ? ', ' + subtitle : ''}`"
    :data-testid="`metric-card-${sanitizedTitle}`"
    :role="clickable ? 'button' : 'region'"
    :tabindex="clickable ? 0 : undefined"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="metric-card__content">
      <div class="metric-card__label" :class="labelClass">
        {{ title }}
      </div>
      <div class="metric-card__value" :class="valueClass">
        {{ value }}
      </div>
      <div v-if="subtitle" class="metric-card__subtitle" :class="subtitleClass">
        {{ subtitle }}
        <v-tooltip v-if="subtitleTooltip" bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              x-small
              class="metric-card__subtitle-icon"
              tabindex="0"
              role="img"
              :aria-label="subtitleTooltip"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>{{ subtitleTooltip }}</span>
        </v-tooltip>
      </div>
    </div>
    <div v-if="icon" class="metric-card__icon">
      <v-icon
        size="24"
        :style="{ color: resolvedIconColor }"
        :aria-hidden="true"
      >
        {{ icon }}
      </v-icon>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
  name: 'MetricCard',

  props: {
    /** Metric title/label */
    title: {
      type: String,
      required: true
    },
    /** Metric value (number or formatted string) */
    value: {
      type: [Number, String],
      required: true
    },
    /** Optional subtitle/description */
    subtitle: {
      type: String,
      default: ''
    },
    /** Optional icon name (mdi-*) */
    icon: {
      type: String,
      default: ''
    },
    /** Icon colour - can be state-dependent (e.g., 'error', 'warning', 'grey') */
    iconColor: {
      type: String,
      default: 'grey'
    },
    /** Apply danger styling to the numeric value */
    dangerValue: {
      type: Boolean,
      default: false
    },
    /** Highlight the card (e.g., for warnings/alerts) */
    highlighted: {
      type: Boolean,
      default: false
    },
    /** Highlight colour when highlighted=true */
    highlightColor: {
      type: String,
      default: 'error'
    },
    /** Custom border colour (overrides default and highlight colour) */
    borderColor: {
      type: String,
      default: ''
    },
    /** Custom aria-label for accessibility */
    ariaLabel: {
      type: String,
      default: ''
    },
    /** Custom CSS class for label */
    labelClass: {
      type: String,
      default: ''
    },
    /** Custom CSS class for value */
    valueClass: {
      type: String,
      default: ''
    },
    /** Custom CSS class for subtitle */
    subtitleClass: {
      type: String,
      default: ''
    },
    /** Optional tooltip text shown via info icon after the subtitle */
    subtitleTooltip: {
      type: String,
      default: ''
    },
    /** Make the card clickable with hover state and pointer cursor */
    clickable: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    sanitizedTitle(): string {
      return this.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    },

    cardClasses(): Record<string, boolean> {
      return {
        'metric-card--highlighted': this.highlighted,
        'metric-card--danger-value': this.dangerValue,
        'metric-card--clickable': this.clickable
      }
    },

    resolvedIconColor(): string {
      // Map common color names to CSS variables or hex values
      const colorMap: Record<string, string> = {
        'error': 'var(--danger)',
        'danger': 'var(--danger)',
        'red': 'var(--danger)',
        'warning': 'var(--warning)',
        'success': 'var(--success)',
        'grey': '#6B7280',
        'gray': '#6B7280'
      }
      return colorMap[this.iconColor.toLowerCase()] || this.iconColor
    },

    highlightStyle(): Record<string, string> {
      // Custom border color takes precedence
      if (this.borderColor) {
        const color = this.resolveColor(this.borderColor)
        return {
          borderLeftColor: color
        }
      }
      // Then check if highlighted
      if (this.highlighted) {
        const color = this.resolveColor(this.highlightColor)
        return {
          borderLeftColor: color
        }
      }
      // Default steel-blue accent stripe when not highlighted
      return {
        borderLeftColor: 'var(--primary)'
      }
    },

    resolveColor() {
      return (color: string): string => {
        // Resolve Vuetify theme names to hex colors (with CSS variable fallbacks)
        const colorMap: Record<string, string> = {
          // Vuetify theme names → hex colors for reliable rendering
          'error': '#DC4751',
          'warning': '#F59E0B',
          'success': '#10B981',
          'primary': '#406A94',
          'info': '#3B82F6',
          'danger': '#DC4751',
          'caution': '#F59E0B',
          // Generic colour names
          'red': '#DC4751',
          'orange': '#F59E0B',
          'green': '#10B981',
          'blue': '#3B82F6',
          'yellow': '#F59E0B'
        }
        return colorMap[color.toLowerCase()] || color
      }
    },

    getHighlightBackground() {
      return (color: string): string => {
        // Create a light background using CSS variables for dark mode support
        const colorMap: Record<string, string> = {
          // CSS variables → light background variables
          'var(--danger)': 'var(--danger-light)',
          'var(--warning)': 'var(--warning-light)',
          'var(--success)': 'var(--success-light)',
          'var(--primary)': 'var(--info-light)',
          'var(--info)': 'var(--info-light)',
          'var(--caution)': 'var(--caution-light)',
          // Vuetify theme names
          'error': 'var(--danger-light)',
          'warning': 'var(--warning-light)',
          'success': 'var(--success-light)',
          'primary': 'var(--info-light)',
          'info': 'var(--info-light)',
          'danger': 'var(--danger-light)',
          'caution': 'var(--caution-light)',
          'red': 'var(--danger-light)',
          'orange': 'var(--warning-light)',
          'green': 'var(--success-light)',
          'blue': 'var(--info-light)',
          'yellow': 'var(--caution-light)'
        }
        return colorMap[color.toLowerCase()] || 'var(--muted)'
      }
    }
  },

  methods: {
    handleClick(event: MouseEvent | KeyboardEvent): void {
      if (this.clickable) {
        this.$emit('click', event)
      }
    }
  }
})
</script>

<style scoped>
.metric-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px;
  background-color: var(--card);
  border: none; /* No visible border except left accent */
  border-left: 4px solid; /* Left accent stripe */
  border-radius: var(--radius-lg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: var(--transition-base);
  height: 100%;
}

/* Clickable card styles */
.metric-card--clickable {
  cursor: pointer;
}

.metric-card--clickable:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.metric-card--clickable:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.metric-card--clickable:focus:not(:focus-visible) {
  outline: none;
}

.metric-card--clickable:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.metric-card__content {
  flex: 1;
  min-width: 0;
}

.metric-card__label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--foreground);
  line-height: 1.4;
}

.metric-card__value {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 12px;
  color: var(--foreground);
  transition: color 0.2s ease;
}

/* Danger state for numeric value */
.metric-card--danger-value .metric-card__value {
  color: var(--danger);
}

.metric-card__subtitle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem; /* Between xs and sm */
  color: var(--muted-foreground); /* Always neutral grey */
  margin-top: 8px;
  line-height: 1.3;
}

.metric-card__subtitle-icon {
  color: var(--muted-foreground);
  cursor: help;
}

.metric-card__icon {
  flex-shrink: 0;
  margin-left: 16px;
  opacity: 1;
}

/* Icon colors are controlled by the resolvedIconColor computed property */
.metric-card__icon >>> .v-icon {
  transition: color 0.2s ease;
}
</style>
